import { NPCS } from '@/data/npcs';
import { getTutorialQuest } from '@/data/tutorialQuests';
import type { NpcId } from '@/lib/assets';
import type { Character } from '@/types/database';

interface TutorialBannerProps {
  character: Character;
  onClick: () => void;
}

const QUEST_1_CONFIG = {
  npcId: 'old_pete' as NpcId,
  title: 'A Word With Old Pete',
  copy: '"Come here, greenhorn. I have a task that\'ll teach you how things work around Cutlass Cove. You interested, or are you just going to stand there?"',
};

export function TutorialBanner({ character, onClick }: TutorialBannerProps): JSX.Element {
  const questNum = character.tutorial_quest_current;

  // Quest 1 uses the existing quest-action system
  if (questNum === 1) {
    const npc = NPCS[QUEST_1_CONFIG.npcId];
    return (
      <BannerButton
        npcId={QUEST_1_CONFIG.npcId}
        npcName={npc.name}
        title={QUEST_1_CONFIG.title}
        copy={QUEST_1_CONFIG.copy}
        onClick={onClick}
      />
    );
  }

  // Quests 2-9 use the tutorial quest data
  const quest = getTutorialQuest(questNum);
  if (!quest) return <></>;

  const npc = NPCS[quest.npc];
  const firstLine = quest.introText.split('\n\n')[0] ?? quest.introText;

  return (
    <BannerButton
      npcId={quest.npc}
      npcName={npc?.name ?? quest.npc}
      title={quest.name}
      copy={`"${firstLine}"`}
      onClick={onClick}
    />
  );
}

function BannerButton({
  npcId,
  npcName,
  title,
  copy,
  onClick,
}: {
  npcId: NpcId;
  npcName: string;
  title: string;
  copy: string;
  onClick: () => void;
}): JSX.Element {
  const npc = NPCS[npcId];
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left relative overflow-hidden rounded-lg border-2 border-candle/50 shadow-[0_0_20px_rgba(232,177,75,0.15)] bg-gradient-to-r from-sea-dark via-sea-deep to-sea-dark px-4 sm:px-5 py-4 text-parchment transition-all duration-200 hover:border-candle hover:shadow-[0_0_24px_rgba(232,177,75,0.25)]"
    >
      <div className="flex items-center gap-4 sm:gap-5">
        <div className="shrink-0">
          <div className="relative h-[60px] w-[60px] sm:h-20 sm:w-20 rounded-full overflow-hidden border-2 border-candle/60 shadow-[0_0_12px_rgba(232,177,75,0.25)]">
            <img
              src={npc.portrait}
              alt={npcName}
              className="h-full w-full object-cover"
              loading="lazy"
              width={80}
              height={80}
            />
          </div>
        </div>
        <div className="min-w-0">
          <p className="font-display tracking-wider text-candle uppercase text-xs sm:text-sm mb-0.5">
            {title}
          </p>
          <p className="text-sm sm:text-base text-parchment-dark leading-relaxed line-clamp-2">
            {copy}
          </p>
        </div>
      </div>
    </button>
  );
}
